import { z } from 'zod';

/**
 * Who inside a publisher may see the personal data of their readers.
 *
 * A newsletter list is the largest pile of personal data the product holds: every address a
 * publisher has collected, with the wording each reader agreed to beside it. Reading it on screen
 * and taking it away as a CSV were both open to anybody who had been added to the team, which sat
 * oddly beside the owners only rule on API keys and on the webhook, and which is a lot of trust to
 * hand somebody by adding them to an org so they can build a quiz.
 *
 * So it is a decision a publisher makes rather than one the product makes for them, with two
 * answers and no middle:
 *
 * - `owners_only`, which is what an org gets when nobody has said otherwise.
 * - `owners_and_members`, for a newsroom where the people who run the newsletter are the people
 *   who make the interactions.
 *
 * One setting for seeing and for exporting, deliberately. Somebody who can read every address on
 * screen does not need an export button to have the list, so gating only the CSV would be theatre.
 *
 * It lives in `packages/core` rather than in `packages/db` because the dashboard renders the same
 * answer this file defines, and a package the dashboard cannot import means the default is written
 * down a second time in the editor and drifts from the one the API applies.
 */
export const subscriberAccessSchema = z.enum(['owners_only', 'owners_and_members']);
export type SubscriberAccess = z.infer<typeof subscriberAccessSchema>;

/**
 * Nobody has chosen, so owners only.
 *
 * The safe default costs nothing today, because no publisher is relying on the old behaviour yet,
 * and it is the only default that can be applied to records written before this setting existed:
 * an absent field has to mean something, and the one meaning that cannot leak anybody's data is
 * the closed one.
 */
export const DEFAULT_SUBSCRIBER_ACCESS: SubscriberAccess = 'owners_only';

/**
 * The whole rule, in one place, so no route can hold a slightly different version of it.
 *
 * An owner is always allowed: they can change this setting, so refusing them would be a lock with
 * the key taped to it. Everybody else is allowed exactly when the publisher has said so.
 *
 * It takes `isOwner` rather than a role because roles are a `packages/db` concept and this file is
 * read by three packages. The caller maps their own idea of a role onto the one question this
 * needs answering.
 */
export function maySeeSubscriberData(options: {
  readonly isOwner: boolean;
  /** What the org has set. Absent means the record predates the setting, so the default applies. */
  readonly access: SubscriberAccess | undefined;
}): boolean {
  if (options.isOwner) return true;
  return (options.access ?? DEFAULT_SUBSCRIBER_ACCESS) === 'owners_and_members';
}
